import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  faDownload,
  faArrowLeft,
  faCaretDown,
  faCaretUp,
  faIdCard,
  faSearch,
  faTimes,
  faSpinner,
  faBars,
  faFilePdf,
  faPaste,
  faClipboard,
  faPencilAlt, // Added faBars if used for mobile menu in WizardForm context (though likely not needed directly here)
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Core dependencies
import { useAuth } from "../../../firebase/AuthProvider"; // Adjust path

// Form related components and types
import { WizardForm } from "./DynamicForm"; // Adjust path

// UI Components
import Banner from "../../ui/Banner/Banner"; // Adjust path
import JsonSyntaxHighlighter from "../../../utilities/formattedDisplayComponents/jsonSyntaxHighlighter"; // Adjust path
import {
  FormConfig,
  FormPageStates,
  FormSubmissionRequest,
  RulesConfig,
} from "../../../interfaces/dynamicForms/Forms";
import {
  getFormConfigs,
  getFormRules,
} from "../../../services/formsService/FormsService";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import "./formfill.css";
import Loader from "../loader/Loader";

// Define the expected structure for the state
type FormConfigsState = Record<string, FormConfig> | null;
type FormRulesState = Record<string, RulesConfig> | null;

// Define styles for the cards
const cardStyles = [
  {
    heroBg: "bg-blue-100",
    bodyBg: "bg-blue-50",
    hoverHeroBg: "hover:bg-blue-200",
    hoverBodyBg: "hover:bg-blue-100",
    iconColor: "text-blue-600",
    hoverIconColor: "group-hover:text-blue-700",
  },
  {
    heroBg: "bg-green-100",
    bodyBg: "bg-green-50",
    hoverHeroBg: "hover:bg-green-200",
    hoverBodyBg: "hover:bg-green-100",
    iconColor: "text-green-600",
    hoverIconColor: "group-hover:text-green-700",
  },
  {
    heroBg: "bg-purple-100",
    bodyBg: "bg-purple-50",
    hoverHeroBg: "hover:bg-purple-200",
    hoverBodyBg: "hover:bg-purple-100",
    iconColor: "text-purple-600",
    hoverIconColor: "group-hover:text-purple-700",
  },
  {
    heroBg: "bg-yellow-100",
    bodyBg: "bg-yellow-50",
    hoverHeroBg: "hover:bg-yellow-200",
    hoverBodyBg: "hover:bg-yellow-100",
    iconColor: "text-yellow-600",
    hoverIconColor: "group-hover:text-yellow-700",
  },
  {
    heroBg: "bg-indigo-100",
    bodyBg: "bg-indigo-50",
    hoverHeroBg: "hover:bg-indigo-200",
    hoverBodyBg: "hover:bg-indigo-100",
    iconColor: "text-indigo-600",
    hoverIconColor: "group-hover:text-indigo-700",
  },
  {
    heroBg: "bg-pink-100",
    bodyBg: "bg-pink-50",
    hoverHeroBg: "hover:bg-pink-200",
    hoverBodyBg: "hover:bg-pink-100",
    iconColor: "text-pink-600",
    hoverIconColor: "group-hover:text-pink-700",
  },
];

// Helper function (if not globally available)
const formatBytes = (bytes: number, decimals = 2): string => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const FormPage: React.FC = () => {
  const { currentUser } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const states: FormPageStates = location.state;

  console.log('States: ', states)

  // --- State ---
  // Form Configurations & Rules fetched from API
  const [formConfigs, setFormConfigs] = useState<FormConfigsState>(null);
  const [formRules, setFormRules] = useState<FormRulesState>(null);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState<boolean>(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Form selection and interaction state
  const [selectedFormKey, setSelectedFormKey] = useState<string | null>(null);
  const [completedData, setCompletedData] = useState<Record<
    string,
    any
  > | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [fileStats, setFileStats] = useState<{ size?: string; type?: string }>(
    {}
  );
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // Search and Navigation state
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [focusedFormIndex, setFocusedFormIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // UI state
  const [banner, setBanner] = useState<{
    message: string;
    status: string;
  } | null>(null);

  // Refs
  const downloadLinkRef = useRef<HTMLAnchorElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const formCardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // --- End State & Refs ---

  // --- Callbacks ---
  const dismissBanner = useCallback(() => setBanner(null), []);

  const handleFormSelect = useCallback((formKey: string) => {
    setSelectedFormKey(formKey);
    setCompletedData(null);
    setDownloadUrl(null);
    setFileStats({});
    setIsSearching(false);
    setSearchTerm("");
    setFocusedFormIndex(null);
  }, []); // No external dependencies needed here

  const handleBackToSelection = useCallback(() => {
    setSelectedFormKey(null);
    setCompletedData(null); // Clear data when going back
    setDownloadUrl(null);
    setFileStats({});
    setFocusedFormIndex(null);
    // Keep search term if user wants to refine search after viewing a form? Optional.
    // setSearchTerm(""); // Uncomment to clear search on back
    // setIsSearching(false); // Uncomment to close search on back

    // If search was active, focus the input again
    if (isSearching) {
      // Timeout ensures input is visible before focus
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isSearching]); // Depends on isSearching state

  const closeSearch = useCallback(() => {
    setIsSearching(false);
    setSearchTerm("");
    setFocusedFormIndex(null);
    // Consider setting focus back to a main element or header if needed
  }, []); // No external dependencies needed

  const postPdfRequest = useCallback(
    async (data: Record<string, any>, instanceId: number) => {
      if (!currentUser) {
        setBanner({ status: "warning", message: "No current user logged in." });
        return null;
      }
      if (!selectedFormKey) {
        setBanner({ status: "warning", message: "No form selected." });
        return null;
      }

      // Ensure API base URL is defined
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!baseUrl) {
        setBanner({
          status: "error",
          message: "API configuration is missing.",
        });
        return null;
      }

      setBanner(null); // Clear previous banners before request
      try {
        const authToken = await currentUser.getIdToken();
        const payload: FormSubmissionRequest = {
          formInstanceId: instanceId,
          pdfTemplateName: `${selectedFormKey}`,
          fields: data,
          isFinalSubmission: true,
          participantId: states?.participantId?.toString() ?? null,
          submittedBy: currentUser.uid,
          // Safely access taskId and ensure it is a number or null to prevent server-side validation errors.
          taskId: states?.taskId ? Number(states.taskId) : null,
        };
        setLoading(true);

        // IMPORTANT: Adjust endpoint as needed
        const response = await fetch(`${baseUrl}/form/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          let errorMsg = `PDF generation failed (Status: ${response.status}).`;
          try {
            const errorData = await response.json();
            errorMsg = `Error: ${errorData?.errorMessage || errorMsg}`;
            setLoading(false);
          } catch {
            /* Ignore parsing error, use status code */
            setLoading(false);
          }
          setBanner({ status: "error", message: errorMsg });
          setLoading(false);

          return null;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        setDownloadUrl(blobUrl);
        setFileStats({
          size: formatBytes(blob.size),
          type: blob.type,
        });
        return { success: true }; // Indicate success
      } catch (err: any) {
        console.error("PDF generation error:", err);
        setBanner({
          status: "error",
          message: `Unexpected error generating PDF: ${err?.message}`,
        });
        return null;
      }
    },
    [currentUser, selectedFormKey]
  ); // Depends on user and selected form

  const handleDownloadClick = useCallback(() => {
    if (downloadUrl && downloadLinkRef.current && selectedFormKey) {
      downloadLinkRef.current.href = downloadUrl;
      downloadLinkRef.current.download = `${selectedFormKey}_Log.pdf`; // Adjust naming if needed
      downloadLinkRef.current.click();
      // Consider revoking the object URL after some time to free memory
      // setTimeout(() => { if (downloadUrl) window.URL.revokeObjectURL(downloadUrl); }, 10000);
    } else {
      // setBanner({
      //   status: "warning",
      //   message: "Download not ready or form not selected.",
      // });
    }
  }, [downloadUrl, selectedFormKey]); // Depends on download state

  const handleFormSubmit = useCallback(
    async (answers: Record<string, any>, instanceId: number) => {
      setCompletedData(answers);
      setBanner(null);
      const result = await postPdfRequest(answers, instanceId);
      if (result?.success) {
        toast.success("Form submitted and PDF generated!");
        setLoading(false);

        if (states)
          navigate(`/task/SSA-${Number(states.taskId)}`);
        else 
          handleDownloadClick();
      } else if (!banner) {
        setLoading(false);

        // Show default failure only if postPdfRequest didn't set one
        // Banner might already be set by postPdfRequest on failure
      }
    },
    [postPdfRequest, banner]
  ); // Depends on postPdfRequest and banner state

  const toggleDetails = useCallback(() => {
    setIsDetailsOpen((prev) => !prev);
  }, []);
  // --- End Callbacks ---

  // --- Effects ---
  // Fetch Form Configs & Rules
  useEffect(() => {
    if (currentUser) {
      const fetchConfigs = async () => {
        setIsLoadingConfigs(true);
        setConfigError(null);
        setFormConfigs(null); // Clear old data before fetching
        setFormRules(null);
        try {
          const token = await currentUser.getIdToken();
          const [configs, rules] = await Promise.all([
            getFormConfigs(token),
            getFormRules(token),
          ]);
          console.log("formConfigs", configs);
          setFormConfigs(configs);
          setFormRules(rules);

          if (states?.FormId) {
            handleFormSelect(states.FormId);
          }
        } catch (error: any) {
          console.error("Error fetching form configurations:", error);
          setConfigError(
            error.message ||
            "Failed to load form configurations. Please try again later."
          );
        } finally {
          setIsLoadingConfigs(false);
        }
      };
      fetchConfigs();
    } else {
      setIsLoadingConfigs(false); // Not loading if no user
      setFormConfigs(null);
      setFormRules(null);
      setConfigError(null); // Clear error if user logs out
    }
  }, [currentUser]); // Re-run when user changes

  // Focus Search Input when opened
  useEffect(() => {
    if (isSearching && !selectedFormKey && focusedFormIndex === null) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isSearching, selectedFormKey, focusedFormIndex]);

  // Global Keydown Listener for Search Activation/Closing
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (selectedFormKey !== null) return; // Only on selection page

      const targetElement = event.target as HTMLElement;
      const isInputFocused = ["INPUT", "TEXTAREA", "SELECT"].includes(
        targetElement.tagName
      );
      const isFormCardFocused = formCardRefs.current.some(
        (ref) => ref === document.activeElement
      );

      // Escape handling
      if (event.key === "Escape") {
        if (focusedFormIndex !== null) {
          setFocusedFormIndex(null);
          searchInputRef.current?.focus();
          event.preventDefault();
        } else if (isSearching) {
          closeSearch();
        }
        return;
      }

      // Activate search on typing if not focused on input/card
      if (
        !isSearching &&
        !isInputFocused &&
        !isFormCardFocused &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        setIsSearching(true);
        setSearchTerm(event.key);
        setFocusedFormIndex(null);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [selectedFormKey, isSearching, focusedFormIndex, closeSearch]); // Added closeSearch

  // --- End Effects ---

  // --- Memoized Derived State ---
  const allAvailableForms = useMemo(() => {
    return formConfigs ? Object.keys(formConfigs) : [];
  }, [formConfigs]);

  const formColorMap = useMemo(() => {
    const map: Record<string, (typeof cardStyles)[0]> = {};
    if (!allAvailableForms.length) return map;
    allAvailableForms.forEach((key, index) => {
      map[key] = cardStyles[index % cardStyles.length];
    });
    return map;
  }, [allAvailableForms]);

  const filteredForms = useMemo(() => {
    if (!formConfigs) return [];
    return allAvailableForms.filter((formKey) => {
      if (!searchTerm) return true;
      const config = formConfigs[formKey];
      const name = formKey.replace(/_/g, " ");
      const description = ""; // Include if available: config?.description || ""
      const term = searchTerm.toLowerCase();
      return (
        name.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term)
      );
    });
  }, [allAvailableForms, searchTerm, formConfigs]);

  // Ensure formCardRefs array resizes with filteredForms
  useEffect(() => {
    formCardRefs.current = formCardRefs.current.slice(0, filteredForms.length);
    if (focusedFormIndex !== null && focusedFormIndex >= filteredForms.length) {
      setFocusedFormIndex(null);
      if (isSearching) searchInputRef.current?.focus();
    }
  }, [filteredForms, focusedFormIndex, isSearching]);

  const currentFormWizardConfig =
    selectedFormKey && formConfigs ? formConfigs[selectedFormKey] : undefined;
  const currentRulesConfig =
    selectedFormKey && formRules ? formRules[selectedFormKey] : undefined;
  // --- End Memoized Derived State ---

  // --- Keyboard Handlers ---
  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && filteredForms.length > 0) {
        event.preventDefault();
        setFocusedFormIndex(0);
        formCardRefs.current[0]?.focus();
      } else if (event.key === "Escape") {
        closeSearch();
      }
    },
    [filteredForms, closeSearch]
  ); // Depends on current filter results & close action

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      // Ensure filteredForms is available before processing
      if (!filteredForms || filteredForms.length === 0) return;

      const currentFormKey = filteredForms[index];
      if (!currentFormKey) return; // Should not happen if index is valid

      // Prevent default for keys we handle
      if (
        ["Enter", " ", "Tab", "ArrowDown", "ArrowUp", "Escape"].includes(
          event.key
        )
      ) {
        event.preventDefault();
      }

      switch (event.key) {
        case "Enter":
        case " ":
          handleFormSelect(currentFormKey);
          break;
        case "Tab": {
          const numForms = filteredForms.length;
          const nextIndex = event.shiftKey
            ? (index - 1 + numForms) % numForms // Shift+Tab (previous)
            : (index + 1) % numForms; // Tab (next)
          setFocusedFormIndex(nextIndex);
          formCardRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowDown": {
          const nextIndex = (index + 1) % filteredForms.length;
          setFocusedFormIndex(nextIndex);
          formCardRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          const prevIndex =
            (index - 1 + filteredForms.length) % filteredForms.length;
          setFocusedFormIndex(prevIndex);
          formCardRefs.current[prevIndex]?.focus();
          break;
        }
        case "Escape":
          setFocusedFormIndex(null);
          searchInputRef.current?.focus();
          break;
        default:
          break; // Do nothing for other keys
      }
      // Dependencies: Need filteredForms to calculate next/prev index and get key,
      // handleFormSelect to select, setFocusedFormIndex to update focus state
    },
    [filteredForms, handleFormSelect, setFocusedFormIndex]
  );

  // --- End Keyboard Handlers ---

  // --- Render Logic ---
  if (isLoadingConfigs) {
    return (
      <div className="flex-grow flex items-center justify-center text-gray-600 p-8">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="mr-3" />
        Loading Form Configurations...
      </div>
    );
  }

  if (configError) {
    return (
      <div className="flex-grow flex flex-col items-center justify-center text-center p-8">
        <p className="text-red-600 font-semibold text-xl mb-4">
          Error Loading Forms
        </p>
        <p className="text-gray-700 mb-6">{configError}</p>
        {/* Maybe add a retry button that calls fetchConfigs again? */}
      </div>
    );
  }

  if (!formConfigs || !formRules) {
    return (
      <div className="flex-grow flex flex-col items-center justify-center text-center p-8">
        <p className="text-gray-600">No form configurations loaded.</p>
      </div>
    );
  }
  return (
    <>
      {loading && <Loader />}
          {banner && (
            <div
              className={`w-full mx-auto max-w-4xl py-4 ${isSearching && !selectedFormKey ? "mt-16" : ""
                }`}
            >
              <Banner
                message={banner.message}
                status={banner.status}
                dismissBanner={dismissBanner}
              />
            </div>
          )}
      <div id="formPage" className="common_card p-0">
        <div className="stepper_form_flx w-auto">
          {isSearching && !selectedFormKey && (
            // <div className="fixed inset-x-0 top-36 p-4 z-50 flex justify-center animate-fade-in-down">
            <div className="absolute w-full max-w-lg left-1/2 transform -translate-x-1/2">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFocusedFormIndex(null);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Type to search forms..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                aria-label="Search Forms"
              />
              {searchTerm && (
                <button
                  onClick={closeSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            // </div>
          )}

          {!selectedFormKey ? (
            <div className={` ${isSearching ? "" : ""}`}>
              <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                  <h2>
                    Select a Form{" "}
                    {filteredForms.length !== allAvailableForms.length &&
                      `(${filteredForms.length} found)`}
                  </h2>
                </div>
                <div className="top_right_flx">
                  <Button
                    variant="primary"
                    onClick={() => navigate("/form/create")}
                  >
                    Create
                  </Button>
                </div>
              </div>
              {filteredForms.length > 0 ? (
                <div className="form_fill_grid">
                  {filteredForms.map((formKey, index) => {
                    const formDisplayConfig = formConfigs[formKey];
                    const styles = formColorMap[formKey] || cardStyles[0];
                    const displayName =
                      formDisplayConfig?.form?.name || formKey.replace(/_/g, " ");
                    // Modify description or add elements
                    const description =
                      "Select to fill out form or edit configuration.";
                    const isFocused = index === focusedFormIndex;

                    return (
                      // Use a div container for layout, place buttons inside
                      <div
                        key={formKey}
                        className={`form_fill_item ${isFocused ? "" : ""}`}
                        ref={(el) =>
                        (formCardRefs.current[index] =
                          el as HTMLButtonElement | null)
                        } // Need to handle ref type carefully if container isn't button
                        // Add tabIndex if div is focusable container
                        tabIndex={0}
                        onKeyDown={(e) => handleCardKeyDown(e as any, index)} // Cast event type if needed
                      >
                        {/* Form Info Section */}
                        <div className={`form_fill_head ${""} `}>
                          <h3>
                            <FontAwesomeIcon
                              icon={faClipboard} // Or a more representative icon
                              className={`text-3xl flex-shrink-0 ${styles.iconColor}`}
                            />{" "}
                            {displayName}
                          </h3>
                        </div>
                        {/* Description */}
                        <div className={`form_fill_body ${""} `}>
                          <p>{description}</p>
                        </div>
                        {/* Action Buttons */}
                        <div className="form_fill_footer">
                          <Button
                            variant="secondary"
                            onClick={() => handleFormSelect(formKey)}
                            className="btn_sm"
                            aria-label={`Fill out form: ${displayName}`}
                          >
                            Fill Out
                          </Button>
                          {/* ADD Edit Button */}
                          <Button
                            className="btn_sm"
                            onClick={() => navigate(`/form/create/${formKey}`)} // Navigate to builder WITH ID
                            aria-label={`Edit form configuration: ${displayName}`}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-600 mt-8">
                  {searchTerm
                    ? `No forms found matching "${searchTerm}".`
                    : "No forms available to display."}
                  {searchTerm && (
                    <button
                      onClick={closeSearch}
                      className="text-indigo-600 hover:underline ml-2"
                    >
                      Clear search?
                    </button>
                  )}
                </p>
              )}
            </div>
          ) : (
            <>
              {currentFormWizardConfig &&
                currentRulesConfig &&
                !(completedData && downloadUrl && selectedFormKey) ? (
                <>
                  {currentFormWizardConfig && currentRulesConfig ? (
                    <>
                      <>
                        <WizardForm
                          formConfig={currentFormWizardConfig}
                          rulesConfig={currentRulesConfig}
                          back={handleBackToSelection}
                          onSubmit={handleFormSubmit}
                          participantId={states?.participantId?.toString()}
                          taskId={states?.taskId}
                          autoPopulateData={states}
                        // formName={currentFormWizardConfig?.formId || selectedFormKey?.replace(/_/g, " ")}
                        />
                      </>
                    </>
                  ) : (
                    <div className="p-6 flex flex-col items-center justify-center text-center flex-grow">
                      <p className="text-red-600 font-semibold mb-4">
                        Error: Could not load configuration for the selected
                        form '{selectedFormKey}'. It might be missing from the
                        fetched data.
                      </p>
                      {!states?.taskId && !states?.participantId && (
                        <button
                          onClick={handleBackToSelection}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium flex items-center transition-colors duration-200"
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            className="mr-2"
                          />
                          Back to Selection
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#f0f2f5] flex-grow">
                  {completedData && downloadUrl && selectedFormKey && (
                    <div className=" max-w-4xl flex-shrink-0 mx-auto">
                      {!states?.taskId && !states?.participantId && (
                        <button
                          onClick={handleBackToSelection}
                          className="bg-gray-300 my-4 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium flex items-center transition-colors duration-200"
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            className="mr-2"
                          />
                          Back to Selection
                        </button>
                      )}
                      <div className="bg-white rounded-md border border-gray-200 p-4 mb-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="flex items-center overflow-hidden mr-2">
                          <FontAwesomeIcon
                            icon={faFilePdf}
                            className="text-red-500 text-3xl mr-3 flex-shrink-0"
                          />
                          <div className="overflow-hidden">
                            <h3 className="font-semibold text-md md:text-lg text-gray-800 break-words">
                              {`${selectedFormKey}_Log.pdf`}
                            </h3>
                            <div className="text-gray-600 text-xs md:text-sm flex items-center mt-1 whitespace-nowrap">
                              {fileStats.size
                                ? `${fileStats.size}`
                                : "Calculating..."}
                              {fileStats.type && (
                                <span className="ml-2 hidden sm:inline">{`(${fileStats.type})`}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <a
                          href={downloadUrl || "#"}
                          ref={downloadLinkRef}
                          style={{ display: "none" }}
                          download={`${selectedFormKey}_Log.pdf`}
                          aria-hidden="true"
                        >
                          Download Link
                        </a>
                        <button
                          onClick={handleDownloadClick}
                          disabled={!downloadUrl}
                          className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center w-full sm:w-auto justify-center transition-colors duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Download PDF"
                          aria-disabled={!downloadUrl}
                        >
                          <FontAwesomeIcon icon={faDownload} className="mr-2" />
                          Download
                        </button>
                      </div>
                      <div className="border bg-white border-gray-300 pt-4">
                        <button
                          onClick={toggleDetails}
                          className="pb-4 px-4 w-full text-left text-indigo-600 hover:text-indigo-500 flex items-center justify-between group"
                          aria-expanded={isDetailsOpen}
                        >
                          <span className="font-semibold text-gray-800">
                            View Submitted Data
                          </span>
                          <FontAwesomeIcon
                            icon={isDetailsOpen ? faCaretUp : faCaretDown}
                            className="text-gray-600 group-hover:text-indigo-500 transition-colors duration-200"
                          />
                        </button>
                        {isDetailsOpen && completedData && (
                          <div className="mt-3 max-h-[30vh] overflow-y-auto rounded border border-gray-200 bg-gray-100 blue-scrollbar">
                            <div className="p-3">
                              <JsonSyntaxHighlighter
                                jsonString={JSON.stringify(
                                  completedData,
                                  null,
                                  2
                                )}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FormPage;
